/**
 * ReceiptPreviewPage — `/mail/receipts` (lookup form) and
 * `/mail/receipt/:trackingNumber` (auto-load via path param).
 *
 * Renders a printable, NIPOST-branded counter receipt for any mail item the
 * operator has scope to read. The full receipt projection lives on
 * `GET /v1/mail-items/:id` (sender, receiver, package, pricing, tracking
 * number, status, registeredAt) — landed in PR 1.
 *
 * Flow:
 *   1. The operator either lands here with a `:trackingNumber` path param
 *      (from SubmitOutcomePanel "Print receipt" CTA) or types a tracking
 *      number into the lookup input.
 *   2. We resolve tracking-number → mail-item-id via
 *      `GET /v1/mail-items/lookup?trackingNumber=...` (operator-only).
 *   3. We fetch the full record via `getMailItem(id)`.
 *   4. The receipt block (`data-testid="receipt-canvas"`) is rendered with
 *      a print-only stylesheet. The "Print" button calls `window.print()`;
 *      the "Save as PDF" hint just nudges operators to use the browser's
 *      built-in PDF backend rather than us shipping a server-side PDF
 *      generator (out of scope for this slice).
 */
import { type JSX, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer, FileText, Search } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  getMailItem,
  lookupMailItemByTracking,
  type MailItemDetail,
} from '../../lib/api/mail-items';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

function pickString(record: Readonly<Record<string, unknown>>, ...keys: string[]): string {
  for (const k of keys) {
    const v = record[k];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return '';
}

function formatNaira(input: {
  amountNgn?: string;
  amountMinor?: number | null;
}): string {
  let value: number | null = null;
  if (typeof input.amountMinor === 'number' && Number.isFinite(input.amountMinor)) {
    value = input.amountMinor / 100;
  } else if (input.amountNgn) {
    const n = Number.parseFloat(input.amountNgn);
    if (Number.isFinite(n)) value = n;
  }
  if (value === null) return '';
  return `₦${value.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ReceiptPreviewPage(): JSX.Element {
  const params = useParams<{ trackingNumber?: string }>();
  const [search] = useSearchParams();
  const initialTracking = params.trackingNumber ?? search.get('tn') ?? '';

  const [trackingInput, setTrackingInput] = useState<string>(initialTracking);
  const [activeTracking, setActiveTracking] = useState<string>(initialTracking);

  // Sync state if the path param changes (route navigation between receipts).
  // We deliberately depend ONLY on `initialTracking` — `activeTracking` is
  // both read and written here and including it would loop.
  useEffect(() => {
    if (initialTracking && initialTracking !== activeTracking) {
      setTrackingInput(initialTracking);
      setActiveTracking(initialTracking);
    }
  }, [initialTracking, activeTracking]);

  const lookupQuery = useQuery({
    queryKey: ['mail', 'lookup', activeTracking],
    queryFn: () => lookupMailItemByTracking(activeTracking),
    enabled: activeTracking.trim().length > 0,
    retry: false,
  });

  const itemId = lookupQuery.data?.id ?? '';
  const itemQuery = useQuery({
    queryKey: ['mail', 'item', itemId],
    queryFn: () => getMailItem(itemId),
    enabled: itemId.length > 0,
    retry: false,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setActiveTracking(trackingInput.trim());
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Receipt"
        description="Render a printable counter receipt by tracking number."
      />

      {/* Print-mode CSS — hide everything outside the receipt canvas. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="receipt-canvas"], [data-testid="receipt-canvas"] * { visibility: visible; }
          [data-testid="receipt-canvas"] {
            position: absolute; left: 0; top: 0; width: 100%;
            box-shadow: none !important; border: none !important;
          }
        }
      `}</style>

      <form
        onSubmit={handleSubmit}
        className="bg-white border border-border rounded-xl p-5 print:hidden"
      >
        <label htmlFor="rp-tn" className="text-xs font-semibold text-muted uppercase">
          Tracking number
        </label>
        <div className="flex gap-3 mt-1">
          <input
            id="rp-tn"
            data-testid="receipt-tracking-input"
            type="text"
            value={trackingInput}
            onChange={(e) => setTrackingInput(e.target.value)}
            placeholder="EE123456785NG"
            className="flex-1 px-3 py-2 text-sm font-mono border border-border rounded-lg uppercase"
          />
          <button
            type="submit"
            data-testid="receipt-lookup-submit"
            disabled={trackingInput.trim().length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50"
          >
            <Search className="w-4 h-4" /> Look up
          </button>
        </div>
      </form>

      {activeTracking === '' ? (
        <DeferredNotice title="Enter a tracking number">
          Type or paste a NIPOST tracking number above (e.g. <code className="font-mono">EE123456785NG</code>) to render the
          counter receipt.
        </DeferredNotice>
      ) : null}

      <ReceiptStates
        activeTracking={activeTracking}
        lookupQuery={lookupQuery}
        itemQuery={itemQuery}
      />
    </div>
  );
}

interface ReceiptStatesProps {
  activeTracking: string;
  lookupQuery: ReturnType<typeof useQuery<{ id: string; trackingNumber: string }>>;
  itemQuery: ReturnType<typeof useQuery<MailItemDetail>>;
}

function ReceiptStates({ activeTracking, lookupQuery, itemQuery }: ReceiptStatesProps): JSX.Element | null {
  if (activeTracking === '') return null;

  if (lookupQuery.isPending) {
    return <p className="text-sm text-muted">Looking up tracking number…</p>;
  }
  if (lookupQuery.isError) {
    const err = lookupQuery.error;
    const denied = err instanceof ApiError && (err.status === 401 || err.status === 403);
    const notFound = err instanceof ApiError && err.status === 404;
    return (
      <DeferredNotice title={denied ? 'Permission required' : notFound ? 'Tracking number not found' : 'Lookup failed'}>
        {(err as Error).message}
      </DeferredNotice>
    );
  }

  if (itemQuery.isPending) {
    return <p className="text-sm text-muted">Loading receipt…</p>;
  }
  if (itemQuery.isError) {
    const err = itemQuery.error;
    return (
      <DeferredNotice title="Receipt fetch failed">{(err as Error).message}</DeferredNotice>
    );
  }

  return <ReceiptCanvas item={itemQuery.data} />;
}

function ReceiptCanvas({ item }: { item: MailItemDetail }): JSX.Element {
  const senderName = pickString(item.sender, 'name', 'fullName');
  const senderPhone = pickString(item.sender, 'phone', 'phoneNumber');
  const senderLine1 = pickString(item.sender, 'addressLine1', 'line1');
  const senderCity = pickString(item.sender, 'city');
  const senderState = pickString(item.sender, 'state');

  const receiverName = pickString(item.receiver, 'name', 'fullName');
  const receiverPhone = pickString(item.receiver, 'phone', 'phoneNumber');
  const receiverLine1 = pickString(item.receiver, 'addressLine1', 'line1');
  const receiverCity = pickString(item.receiver, 'city');
  const receiverState = pickString(item.receiver, 'state');

  const weight = pickString(item.package, 'weightKg');
  const desc = pickString(item.package, 'contentDescription', 'description');
  const declared = pickString(item.package, 'declaredValueNgn', 'declaredValue');
  const dims = item.package['dimensionsCm'] as
    | { length?: string; width?: string; height?: string }
    | undefined;
  const dimsStr = dims && (dims.length || dims.width || dims.height)
    ? `${dims.length ?? '?'} × ${dims.width ?? '?'} × ${dims.height ?? '?'} cm`
    : '';

  const amountNgn = pickString(item.pricing, 'amountNgn', 'amount', 'totalNgn');
  const amountMinorRaw = item.pricing['amountMinor'];
  const amountMinor =
    typeof amountMinorRaw === 'number'
      ? amountMinorRaw
      : typeof amountMinorRaw === 'string'
        ? Number.parseInt(amountMinorRaw, 10)
        : null;
  const formattedAmount = formatNaira({ amountNgn, amountMinor });
  const method = pickString(item.pricing, 'method', 'methodKind', 'paymentMethod');
  const reference = pickString(item.pricing, 'reference', 'paymentReference');
  const payStatus = pickString(item.pricing, 'status', 'paymentStatus');

  return (
    <>
      <div className="flex items-center justify-end gap-3 print:hidden">
        <button
          type="button"
          data-testid="receipt-print"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg"
        >
          <Printer className="w-4 h-4" /> Print
        </button>
        <span className="text-xs text-muted">
          Tip: choose <strong>Save as PDF</strong> in the print dialog for an emailable copy.
        </span>
      </div>

      <div
        data-testid="receipt-canvas"
        className="max-w-md mx-auto bg-white border border-border rounded-xl p-6 shadow-sm"
      >
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-nipost-green rounded-lg mb-2">
            <span className="text-white text-xl font-bold">N</span>
          </div>
          <p className="text-sm font-bold text-gray-900">NIPOST · UPMP</p>
          <p className="text-[11px] text-muted">Counter receipt</p>
        </div>

        <div className="border-t border-dashed border-border pt-3 space-y-1 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-muted">Tracking</span>
            <span
              data-testid="receipt-tracking"
              className="font-mono text-base font-bold tracking-wider"
            >
              {item.trackingNumber}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Status</span>
            <StatusBadge status={item.status} />
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Service</span>
            <span>{item.serviceClass}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted">Date</span>
            <span>{new Date(item.registeredAt).toLocaleString()}</span>
          </div>
        </div>

        <ReceiptParty
          title="Sender"
          name={senderName}
          phone={senderPhone}
          line1={senderLine1}
          city={senderCity}
          state={senderState}
          testId="receipt-sender"
        />
        <ReceiptParty
          title="Receiver"
          name={receiverName}
          phone={receiverPhone}
          line1={receiverLine1}
          city={receiverCity}
          state={receiverState}
          testId="receipt-receiver"
        />

        <div
          data-testid="receipt-package"
          className="border-t border-dashed border-border mt-3 pt-3 space-y-1 text-xs"
        >
          <p className="font-semibold text-gray-900">Package</p>
          {weight ? (
            <div className="flex justify-between"><span className="text-muted">Weight</span><span>{weight} kg</span></div>
          ) : null}
          {dimsStr ? (
            <div className="flex justify-between"><span className="text-muted">Dimensions</span><span>{dimsStr}</span></div>
          ) : null}
          {declared ? (
            <div className="flex justify-between">
              <span className="text-muted">Declared value</span>
              <span>{formatNaira({ amountNgn: declared })}</span>
            </div>
          ) : null}
          {desc ? (
            <div className="flex justify-between gap-3">
              <span className="text-muted">Contents</span>
              <span className="text-right break-words max-w-[60%]">{desc}</span>
            </div>
          ) : null}
        </div>

        <div
          data-testid="receipt-payment"
          className="border-t border-dashed border-border mt-3 pt-3 space-y-1 text-xs"
        >
          <p className="font-semibold text-gray-900">Payment</p>
          {formattedAmount ? (
            <div className="flex justify-between text-sm font-bold">
              <span>Amount</span>
              <span data-testid="receipt-amount" className="font-mono">{formattedAmount}</span>
            </div>
          ) : (
            <p className="text-muted italic">No payment recorded yet.</p>
          )}
          {method ? (
            <div className="flex justify-between"><span className="text-muted">Method</span><span>{method}</span></div>
          ) : null}
          {reference ? (
            <div className="flex justify-between"><span className="text-muted">Reference</span><span className="font-mono">{reference}</span></div>
          ) : null}
          {payStatus ? (
            <div className="flex justify-between"><span className="text-muted">Status</span><span className="font-mono">{payStatus}</span></div>
          ) : null}
        </div>

        <div className="border-t border-dashed border-border mt-3 pt-3 text-center">
          <FileText className="w-3 h-3 inline text-muted" />
          <p className="text-[10px] text-muted mt-1">Thank you for choosing NIPOST.</p>
          <p className="text-[10px] text-muted">Item id: <span className="font-mono">{item.id}</span></p>
        </div>
      </div>
    </>
  );
}

interface ReceiptPartyProps {
  title: string;
  name: string;
  phone: string;
  line1: string;
  city: string;
  state: string;
  testId: string;
}

function ReceiptParty({ title, name, phone, line1, city, state, testId }: ReceiptPartyProps): JSX.Element | null {
  if (!name && !phone && !line1) return null;
  const where = [city, state].filter(Boolean).join(', ');
  return (
    <div
      data-testid={testId}
      className="border-t border-dashed border-border mt-3 pt-3 space-y-0.5 text-xs"
    >
      <p className="font-semibold text-gray-900">{title}</p>
      {name ? <p className="text-gray-900">{name}</p> : null}
      {phone ? <p className="font-mono text-muted">{phone}</p> : null}
      {line1 ? <p className="text-muted">{line1}</p> : null}
      {where ? <p className="text-muted">{where}</p> : null}
    </div>
  );
}
